import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Calendar,
  Mail,
  Phone,
  Users,
  Save,
  Loader2,
  AlertTriangle,
  Info,
  Send,
  ShieldCheck,
  Trash2,
  Globe,
  Clock,
  ChevronDown,
  ChevronUp,
  Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { useLifely } from '../context/LifelyContext';
import { toast } from 'sonner';
import api, { continuityAPI } from '../services/api';

const INTERVAL_UNITS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
];

// Common timezones
const TIMEZONE_OPTIONS = [
  { value: 'Pacific/Auckland', label: 'Auckland (GMT+12/+13)' },
  { value: 'Australia/Sydney', label: 'Sydney (GMT+10/+11)' },
  { value: 'Australia/Perth', label: 'Perth (GMT+8)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (GMT+9)' },
  { value: 'Asia/Singapore', label: 'Singapore (GMT+8)' },
  { value: 'Asia/Dubai', label: 'Dubai (GMT+4)' },
  { value: 'Europe/London', label: 'London (GMT/+1)' },
  { value: 'Europe/Paris', label: 'Paris (GMT+1/+2)' },
  { value: 'Europe/Berlin', label: 'Berlin (GMT+1/+2)' },
  { value: 'America/New_York', label: 'New York (GMT-5/-4)' },
  { value: 'America/Chicago', label: 'Chicago (GMT-6/-5)' },
  { value: 'America/Denver', label: 'Denver (GMT-7/-6)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (GMT-8/-7)' },
  { value: 'UTC', label: 'UTC (GMT+0)' },
];

// Notification time presets
const NOTIFICATION_TIMES = [
  { value: 'morning', label: 'Morning', time: '9:00 AM' },
  { value: 'afternoon', label: 'Afternoon', time: '2:00 PM' },
  { value: 'evening', label: 'Evening', time: '7:00 PM' },
];

const ContinuitySettings = () => {
  const navigate = useNavigate();
  const { 
    user, 
    continuityConfig,
    updateContinuityConfig,
    saveContinuitySettings,
    deactivateContinuity,
    fetchContinuity,
    shareSettings,
    fetchSharing,
    deleteShareSetting,
    getContactById,
    userContactPrefs,
    isAuthenticated,
    loading,
  } = useLifely();
  
  const [saving, setSaving] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  
  // Form state
  const [checkInInterval, setCheckInInterval] = useState(1);
  const [checkInUnit, setCheckInUnit] = useState('months');
  const [useEmail, setUseEmail] = useState(true);
  const [useSms, setUseSms] = useState(false);
  const [timezone, setTimezone] = useState('UTC');
  const [notificationTime, setNotificationTime] = useState('morning');
  const [reminderCount, setReminderCount] = useState(3);
  const [escalationMessage, setEscalationMessage] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Email verification
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationSending, setVerificationSending] = useState(false);
  // Refs so the polling loop sees the latest verified state and can be cleared on unmount.
  const emailVerifiedRef = useRef(false);
  const pollIntervalRef = useRef(null);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (isAuthenticated && !dataLoaded) {
        try {
          await Promise.all([fetchContinuity(), fetchSharing()]);
          setDataLoaded(true);
        } catch (err) {
          console.error('Failed to load data:', err);
        }
      }
    };
    loadData();
  }, [isAuthenticated, dataLoaded, fetchContinuity, fetchSharing]);

  // Auto-detect timezone on mount (only if not already saved)
  useEffect(() => {
    try {
      const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const found = TIMEZONE_OPTIONS.find(tz => tz.value === detectedTz);
      if (found) setTimezone(detectedTz);
    } catch (e) { /* ignore */ }
  }, []);

  // Populate form from existing config
  useEffect(() => {
    if (continuityConfig && dataLoaded) {
      setCheckInInterval(continuityConfig.checkInInterval || continuityConfig.checkInFrequency || 1);
      setCheckInUnit(continuityConfig.checkInUnit || 'months');
      setUseEmail(continuityConfig.useEmail !== false);
      setUseSms(continuityConfig.useSms || false);
      // Only override auto-detected timezone if one is already saved
      if (continuityConfig.timezone && continuityConfig.timezone !== 'UTC') {
        setTimezone(continuityConfig.timezone);
      } else if (continuityConfig.timezone === 'UTC') {
        // Keep auto-detected value, don't override with UTC default
      }
      setNotificationTime(continuityConfig.notificationTime || continuityConfig.notification_time || 'morning');
      setReminderCount(continuityConfig.reminderCount || continuityConfig.reminder_count || 3);
      setEscalationMessage(continuityConfig.escalationMessage || continuityConfig.escalation_message || '');
      setEmailVerified(continuityConfig.email_verified === 1);
      emailVerifiedRef.current = continuityConfig.email_verified === 1;
    }
  }, [continuityConfig, dataLoaded]);

  // Clear any active verification poll on unmount
  useEffect(() => () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
  }, []);

  // Get nominees from share settings
  const nominees = shareSettings?.filter(
    s => s.shareTrigger === 'continuity' || s.shareTrigger === 'both'
  ) || [];

  // Get email to use - respect the useAlternativeEmail preference
  const targetEmail = (userContactPrefs?.useAlternativeEmail && userContactPrefs?.alternativeEmail) 
    ? userContactPrefs.alternativeEmail 
    : user?.email;
  const hasPhone = !!userContactPrefs?.phone;

  // Handle email verification
  const handleSendVerification = async () => {
    try {
      setVerificationSending(true);
      const response = await continuityAPI.sendVerification({ source: 'settings' });

      if (response.success) {
        toast.success('Verification email sent! Check your inbox.');
        startVerificationPolling();
      }
    } catch (error) {
      toast.error('Failed to send verification email');
    } finally {
      setVerificationSending(false);
    }
  };

  // Poll for email verification status after sending (every 5s for up to 5 minutes)
  const startVerificationPolling = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    let attempts = 0;
    const maxAttempts = 60;
    const interval = setInterval(async () => {
      attempts++;
      if (attempts >= maxAttempts || emailVerifiedRef.current) {
        clearInterval(interval);
        return;
      }
      try {
        const res = await api.get('/api/continuity');
        if (res.data.continuity?.email_verified === 1) {
          setEmailVerified(true);
          emailVerifiedRef.current = true;
          clearInterval(interval);
          toast.success('Email verified successfully!');
        }
      } catch {
        // Silently continue polling
      }
    }, 5000);
    pollIntervalRef.current = interval;
  };

  // Save settings
  const handleSave = async () => {
    if (!useEmail) {
      toast.error('Please select at least one contact method');
      return;
    }

    if (nominees.length === 0) {
      toast.error('Please add at least one nominee in the Sharing settings');
      return;
    }

    try {
      setSaving(true);
      
      await updateContinuityConfig({
        checkInFrequency: checkInInterval,
        checkInInterval,
        checkInUnit,
        useEmail,
        useSms: false, // SMS disabled for now
        timezone,
        notificationTime,
        reminderCount,
        escalationMessage,
        isConfigured: true,
        isActive: true,
      });
      
      await saveContinuitySettings();
      
      toast.success('Settings saved - your check-in timer has been reset from now');
      navigate('/continuity');
    } catch (err) {
      toast.error('Failed to save settings');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Deactivate continuity
  const handleDeactivate = async () => {
    try {
      setSaving(true);
      
      await updateContinuityConfig({
        isConfigured: false,
        isActive: false,
      });
      
      toast.success('Continuity deactivated');
      setShowDeactivateDialog(false);
      navigate('/continuity');
    } catch (err) {
      toast.error('Failed to deactivate continuity');
    } finally {
      setSaving(false);
    }
  };

  // Remove nominee
  const handleRemoveNominee = async (shareId) => {
    try {
      await deleteShareSetting(shareId);
      toast.success('Nominee removed');
    } catch (err) {
      toast.error('Failed to remove nominee');
    }
  };

  // Show loading
  if ((loading.continuity || loading.sharing) && !dataLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="continuity-settings">
      {/* Back link */}
      <Link 
        to="/continuity" 
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Continuity
      </Link>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-semibold">Continuity</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your heartbeat settings and nominees.
        </p>
      </div>

      {/* How the cycle works (compact) */}
      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted border border-border" data-testid="escalation-info-banner">
        <Info className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <p className="text-sm text-muted-foreground">
          Check-ins sent at your preferred frequency. Missed reminders escalate over 24hrs. Nominees notified only if all reminders go unanswered.
        </p>
      </div>

      {/* Email delivery tip */}
      <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800" data-testid="email-whitelist-tip">
        <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
        <p className="text-sm text-blue-800 dark:text-blue-200">
          Add <strong className="text-blue-900 dark:text-blue-100">noreply@mail.lifely.guide</strong> to your contacts so check-in reminders don't go to spam.
        </p>
      </div>

      {/* Check-in Frequency */}
      <Card className="border border-border border-l-4 border-l-primary/30">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base font-medium">
            <Calendar className="w-4 h-4 text-primary" />
            Check-in Frequency
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm">Check in every</span>
            <Input
              type="number"
              min="1"
              max="365"
              value={checkInInterval}
              onChange={(e) => setCheckInInterval(parseInt(e.target.value) || 1)}
              className="w-20 h-9"
            />
            <Select value={checkInUnit} onValueChange={setCheckInUnit}>
              <SelectTrigger className="w-28 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INTERVAL_UNITS.map(u => (
                  <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            You'll receive a reminder to check in at this interval.
          </p>
        </CardContent>
      </Card>

      {/* Timezone & Notification Time */}
      <Card className="border border-border border-l-4 border-l-primary/30">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base font-medium">
            <Globe className="w-4 h-4 text-primary" />
            Timezone & Reminder Time
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm">Your timezone</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger>
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONE_OPTIONS.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-sm flex items-center gap-2">
              <Clock className="w-3 h-3" />
              Preferred reminder time
            </Label>
            <RadioGroup value={notificationTime} onValueChange={setNotificationTime} className="flex gap-2">
              {NOTIFICATION_TIMES.map((time) => (
                <div key={time.value} className="flex-1">
                  <RadioGroupItem
                    value={time.value}
                    id={`settings-time-${time.value}`}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={`settings-time-${time.value}`}
                    className={`
                      flex flex-col items-center p-2 rounded-lg border cursor-pointer transition-all text-center
                      ${notificationTime === time.value 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                      }
                    `}
                  >
                    <p className="font-medium text-xs">{time.label}</p>
                    <p className="text-xs text-muted-foreground">{time.time}</p>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          <p className="text-xs text-muted-foreground">
            Reminders will be sent at your preferred time in your local timezone.
          </p>
        </CardContent>
      </Card>

      {/* Contact Methods */}
      <Card className="border border-border border-l-4 border-l-primary/30">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base font-medium">
            <Mail className="w-4 h-4 text-primary" />
            Contact Methods
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Email */}
          <div className={`p-3 rounded-lg border ${useEmail ? 'border-primary/50 bg-primary/5' : 'border-border'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Checkbox checked={useEmail} onCheckedChange={setUseEmail} />
                <div>
                  <p className="font-medium text-sm">Email</p>
                  <p className="text-xs text-muted-foreground">{targetEmail}</p>
                </div>
              </div>
              {useEmail && (
                emailVerified ? (
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-xs">
                    <ShieldCheck className="w-3 h-3 mr-1" /> Verified
                  </Badge>
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="h-7 text-xs"
                    onClick={handleSendVerification}
                    disabled={verificationSending}
                  >
                    {verificationSending ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Send className="w-3 h-3 mr-1" />
                    )}
                    Verify
                  </Button>
                )
              )}
            </div>
          </div>

          {/* SMS - Plus plan only */}
          <div className="p-3 rounded-lg border border-border bg-muted/30 opacity-70">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Checkbox checked={false} disabled />
                <div>
                  <p className="font-medium text-sm flex items-center gap-2">
                    <Phone className="w-3 h-3" />
                    SMS
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Text message reminders
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                Plus Plan
              </Badge>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Change your contact details in <Link to="/contacts" className="text-primary hover:underline">Contacts</Link>.
          </p>
        </CardContent>
      </Card>

      {/* Nominees */}
      <Card className="border border-border border-l-4 border-l-primary/30">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base font-medium">
            <Users className="w-4 h-4 text-primary" />
            Nominees
            <Badge variant="secondary" className="ml-auto">{nominees.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {nominees.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-3">No nominees configured</p>
              <Button variant="outline" size="sm" asChild>
                <Link to="/sharing">Add in Sharing</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {nominees.map((share) => {
                const contact = getContactById(share.contactId);
                return (
                  <div key={share.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                    <div>
                      <p className="font-medium text-sm">{share.contactName || contact?.name || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">
                        {share.shareType === 'all_maps' ? 'All maps' : `${share.selectedMaps?.length || 0} maps`}
                        {share.includeWillStatus && ' + Formal arrangements'}
                      </p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemoveNominee(share.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
              <Button variant="outline" size="sm" asChild className="w-full">
                <Link to="/sharing">Manage in Sharing</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Advanced Settings */}
      <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
        <Card className="border border-border">
          <CollapsibleTrigger asChild>
            <CardHeader className="pb-3 cursor-pointer hover:bg-muted/30 transition-colors">
              <CardTitle className="flex items-center justify-between text-base font-medium">
                <div className="flex items-center gap-2">
                  <Settings className="w-4 h-4 text-primary" />
                  Escalation Settings
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-normal text-muted-foreground">
                    {reminderCount} reminders before notifying
                  </span>
                  {showAdvanced ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-6 pt-0">
              {/* Reminder count */}
              <div className="space-y-3">
                <Label>Reminders before notifying contacts</Label>
                <p className="text-xs text-muted-foreground">
                  After this many missed check-ins, your nominees will be notified automatically.
                </p>
                <RadioGroup 
                  value={String(reminderCount)} 
                  onValueChange={(v) => setReminderCount(parseInt(v))}
                  className="flex flex-wrap gap-3"
                >
                  {[1, 2, 3, 4, 5].map(num => (
                    <div key={num} className="flex items-center space-x-2">
                      <RadioGroupItem value={String(num)} id={`reminder-${num}`} />
                      <Label htmlFor={`reminder-${num}`} className="font-normal cursor-pointer">
                        {num}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Custom message */}
              <div className="space-y-3">
                <Label htmlFor="escalation-message">Custom message to nominees (optional)</Label>
                <p className="text-xs text-muted-foreground">
                  This message will be included in the notification email sent to your nominees.
                </p>
                <Textarea
                  id="escalation-message"
                  value={escalationMessage}
                  onChange={(e) => setEscalationMessage(e.target.value)}
                  placeholder="Enter a personal message for your nominees..."
                  className="min-h-[100px]"
                />
              </div>

              {/* Info box */}
              <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <p className="text-xs text-muted-foreground">
                  <strong>How it works:</strong> Reminders are automatically spaced across your check-in period. 
                  For example, with a monthly check-in and 3 reminders, you'll receive a reminder approximately 
                  every 8 days if you don't respond.
                </p>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Action buttons */}
      <div className="flex items-center justify-between pt-4">
        <Button 
          variant="outline" 
          className="text-destructive border-destructive/50 hover:bg-destructive/10"
          onClick={() => setShowDeactivateDialog(true)}
        >
          <AlertTriangle className="w-4 h-4 mr-2" />
          Deactivate
        </Button>

        <Button 
          onClick={handleSave} 
          disabled={saving || (!useEmail && !useSms) || nominees.length === 0}
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      {/* Warning if no nominees */}
      {nominees.length === 0 && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            You need at least one nominee to save your settings. Add nominees in the{' '}
            <Link to="/sharing" className="underline">Sharing</Link> page.
          </p>
        </div>
      )}

      {/* Deactivate Dialog */}
      <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Deactivate Continuity?
            </DialogTitle>
            <DialogDescription className="pt-2">
              This will stop all check-in reminders. Your nominees will not be notified if you stop responding.
              You can reactivate continuity at any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowDeactivateDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeactivate}
              disabled={saving}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContinuitySettings;
